import { useCallback, useMemo, useState } from 'react';

import { useExistingAnalyticalDatasetNames } from './useExistingAnalyticalDatasetNames';
import { useRenameAnalyticalDatasetMutation } from '../lib/api/useRenameAnalyticalDatasetMutation';

interface UseRenameAnalyticalDatasetDialogArgs {
  projectId: string;
  analyticalDatasetProfileId: string;
  currentName: string;
  onClose: () => void;
}

export interface UseRenameAnalyticalDatasetDialogResult {
  name: string;
  onChangeName: (id: string, value: string) => void;
  onSubmit: () => Promise<void>;
  closeDialog: () => void;
  isPending: boolean;
  submittingError: { message: string } | undefined;
  /** True when the entered name already belongs to another AD in this project. */
  isDuplicateName: boolean;
  isValidToSubmit: boolean;
}

/**
 * Behaviour for the rename-analytical-dataset dialog: name entry (pre-filled with
 * the current name), client-side validation (non-empty, changed, not a duplicate of
 * another AD in the project), and the rename mutation. Intl-free so the view owns
 * copy. A duplicate name is blocked inline; the server check stays as the
 * authoritative guard for the concurrent-rename race.
 */
export const useRenameAnalyticalDatasetDialog = ({
  projectId,
  analyticalDatasetProfileId,
  currentName,
  onClose,
}: UseRenameAnalyticalDatasetDialogArgs): UseRenameAnalyticalDatasetDialogResult => {
  const [name, setName] = useState(currentName);
  const { mutateAsync, isPending, error, reset } = useRenameAnalyticalDatasetMutation();
  // Exclude self so keeping the current name isn't flagged as a clash.
  const existingNames = useExistingAnalyticalDatasetNames(projectId, analyticalDatasetProfileId);

  const onChangeName = useCallback((_id: string, value: string) => {
    setName(value);
  }, []);

  const trimmed = name.trim();
  const isDuplicateName = useMemo(
    () => Boolean(trimmed) && existingNames.has(trimmed.toLowerCase()),
    [existingNames, trimmed]
  );

  const onSubmit = useCallback(async () => {
    const next = name.trim();
    if (!next || next === currentName || existingNames.has(next.toLowerCase())) return;
    // Let a rejection propagate so the dialog shows the error instead of closing.
    await mutateAsync({ projectId, analyticalDatasetProfileId, name: next });
  }, [projectId, analyticalDatasetProfileId, name, currentName, existingNames, mutateAsync]);

  const closeDialog = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  return {
    name,
    onChangeName,
    onSubmit,
    closeDialog,
    isPending,
    submittingError: error ? { message: error.message } : undefined,
    isDuplicateName,
    isValidToSubmit: Boolean(trimmed) && trimmed !== currentName && !isDuplicateName,
  };
};
